/**
 * CustomerJourney — pinned horizontal scroll journey.
 * The page scroll travels the timeline sideways while a flowing line
 * draws itself through every step, led by a glowing pen orb.
 */
import { useEffect, useRef, useState } from 'react';
import {
  HeartHandshake, FileSignature, Camera, Zap, Globe, Clapperboard,
  CalendarCheck, RefreshCw, KeyRound, TrendingUp, MapPin, Play, X,
} from 'lucide-react';

const ITEMS = [
  { n: 1, title: 'Valuation Video', desc: 'A personalised touch point that lands before you do — winning the instruction with a video made just for the vendor.', icon: HeartHandshake, slots: ['example-post-valuation'] },
  { milestone: 'Contracts Signed', icon: FileSignature },
  { n: 2, title: 'NicheCom Visit', desc: 'One visit captures everything — photography, video, floorplan and EPC in a single appointment.', icon: Camera, slots: [] },
  { n: 3, title: 'Sneak Peek', desc: 'Quick delivery for hot buyers — tease the listing on social while the full edit is still in the studio.', icon: Zap, slots: ['example-sneak-peek', 'intro-social-go'] },
  { milestone: 'LIVE on Portals', icon: Globe },
  { n: 4, title: 'Property Video', desc: 'The listing hits social media in style — elevated with NarraTour or PresenTour storytelling.', icon: Clapperboard, slots: ['example-property-video', 'example-narratour', 'intro-narratour', 'example-presentour'] },
  { milestone: 'Viewings booked in', icon: CalendarCheck },
  { n: 5, title: 'Post Viewing Video', desc: 'Reuse the asset — follow up every viewing with a polished reminder that keeps buyers warm.', icon: RefreshCw, slots: ['example-post-viewing', 'intro-feedback'] },
  { milestone: 'Property Sold', icon: KeyRound },
  { n: 6, title: 'Market Update Video', desc: 'Keep your audience engaged between listings with regular, branded market updates.', icon: TrendingUp, slots: [] },
  { n: 7, title: 'Town Overview Video', desc: 'Narrated by your agent — own your patch and become the local authority.', icon: MapPin, slots: [] },
];

/* ── layout maths (module scope, fixed design coordinates) ── */
const STAGE_H = 600;
const START_PAD = 340;
const END_PAD = 420;
const STEP_W = 480;
const MILE_W = 310;
const Y_HIGH = 200;
const Y_LOW = 400;
const Y_MID = 300;

let cursor = START_PAD;
let stepIdx = 0;
const NODES = ITEMS.map((it) => {
  const w = it.milestone ? MILE_W : STEP_W;
  const cx = cursor + w / 2;
  cursor += w;
  let cy;
  if (it.milestone) {
    cy = Y_MID;
  } else {
    cy = stepIdx % 2 === 0 ? Y_HIGH : Y_LOW;
    stepIdx += 1;
  }
  return { ...it, cx, cy };
});
const TOTAL_W = cursor + END_PAD;

// Flowing S-curve path through every node, with a wandering start and finish
const buildPath = () => {
  const pts = [{ x: 40, y: Y_MID + 40 }, ...NODES.map((n) => ({ x: n.cx, y: n.cy })), { x: TOTAL_W - 60, y: Y_MID - 30 }];
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1];
    const q = pts[i];
    const mx = (p.x + q.x) / 2;
    d += ` C ${mx} ${p.y}, ${mx} ${q.y}, ${q.x} ${q.y}`;
  }
  return d;
};
const PATH_D = buildPath();

export const CustomerJourney = ({ videos }) => {
  const runwayRef = useRef(null);
  const trackRef = useRef(null);
  const pathRef = useRef(null);
  const glowRef = useRef(null);
  const penRef = useRef(null);
  const barRef = useRef(null);
  const hintRef = useRef(null);
  const [runwayH, setRunwayH] = useState('320vh');
  const [lightbox, setLightbox] = useState(null);

  const videoMap = {};
  [...((videos && videos.introductions) || []), ...((videos && videos.examples) || [])].forEach((v) => {
    if (v.url) videoMap[v.id] = v;
  });

  useEffect(() => {
    const path = pathRef.current;
    const glow = glowRef.current;
    if (!path) return;
    const pathLen = path.getTotalLength();
    path.style.strokeDasharray = pathLen;
    glow.style.strokeDasharray = pathLen;

    const setRunway = () => setRunwayH(`calc(100vh + ${Math.max(TOTAL_W - window.innerWidth, 600)}px)`);
    setRunway();

    let raf = null;
    const update = () => {
      raf = null;
      const runway = runwayRef.current;
      const track = trackRef.current;
      if (!runway || !track) return;
      const rect = runway.getBoundingClientRect();
      const vw = window.innerWidth;
      const scrollable = Math.max(TOTAL_W - vw, 600);
      const p = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
      const tx = p * scrollable;
      track.style.transform = `translate3d(${-tx}px, 0, 0)`;

      // Draw the line just ahead of the viewport's focal point
      const tipX = Math.min(TOTAL_W, tx + vw * 0.72);
      const drawn = Math.min(1, Math.max(0.02, tipX / TOTAL_W));
      const offset = pathLen * (1 - drawn);
      path.style.strokeDashoffset = offset;
      glow.style.strokeDashoffset = offset;

      // Pen orb rides the tip of the line
      if (penRef.current) {
        const pt = path.getPointAtLength(pathLen * drawn);
        penRef.current.style.transform = `translate3d(${pt.x}px, ${pt.y}px, 0)`;
        penRef.current.style.opacity = drawn >= 0.995 ? '0' : '1';
      }

      // Reveal items the line has reached
      track.querySelectorAll('[data-cx]').forEach((el) => {
        if (tipX >= parseFloat(el.dataset.cx) - 40) el.classList.add('is-on');
      });

      if (barRef.current) barRef.current.style.width = `${(p * 100).toFixed(1)}%`;
      if (hintRef.current) hintRef.current.style.opacity = p > 0.04 ? '0' : '1';
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    const onResize = () => { setRunway(); onScroll(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="cjh" data-testid="customer-journey">
      <div className="cjh-runway" ref={runwayRef} style={{ height: runwayH }}>
        <div className="cjh-sticky">
          <div className="cjh-head">
            <span className="cjh-eyebrow">From valuation to sold</span>
            <h2 className="cjh-title">The Customer <em>Journey</em></h2>
          </div>

          <div className="cjh-hint" ref={hintRef}>
            <span className="cjh-hint-mouse"><span /></span>
            <span className="cjh-hint-text">Keep scrolling to travel the journey</span>
            <svg className="cjh-hint-chevrons" width="14" height="18" viewBox="0 0 14 18" fill="none">
              <path d="M2 3 L7 8 L12 3" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 10 L7 15 L12 10" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <div className="cjh-viewport">
            <div className="cjh-track" ref={trackRef} style={{ width: TOTAL_W, height: STAGE_H }}>
              <svg className="cjh-svg" width={TOTAL_W} height={STAGE_H} viewBox={`0 0 ${TOTAL_W} ${STAGE_H}`} fill="none">
                <path className="cjh-path-base" d={PATH_D} />
                <path ref={glowRef} className="cjh-path-glow" d={PATH_D} />
                <path ref={pathRef} className="cjh-path" d={PATH_D} />
              </svg>

              <div className="cjh-pen" ref={penRef} />

              {NODES.map((item, i) => {
                const Icon = item.icon;
                if (item.milestone) {
                  return (
                    <div
                      key={`m-${i}`}
                      className="cjh-milestone"
                      style={{ left: item.cx, top: item.cy }}
                      data-cx={item.cx}
                      data-testid={`cj-milestone-${item.milestone.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className="cjh-milestone-pill">
                        <Icon size={14} />
                        {item.milestone}
                      </span>
                    </div>
                  );
                }
                const isHigh = item.cy === Y_HIGH;
                const chips = [];
                const seen = new Set();
                (item.slots || []).forEach((sid) => {
                  const v = videoMap[sid];
                  if (v && !seen.has(v.label)) { seen.add(v.label); chips.push(v); }
                });
                return (
                  <div key={`s-${item.n}`} data-testid={`cj-step-${item.n}`}>
                    <div
                      className="cjh-node"
                      style={{ left: item.cx, top: item.cy }}
                      data-cx={item.cx}
                    >
                      <Icon size={20} />
                      <span className="cjh-node-num">{item.n}</span>
                    </div>
                    <div
                      className={`cjh-card ${isHigh ? 'is-below' : 'is-above'} ${item.n % 2 ? 'tilt-l' : 'tilt-r'}`}
                      style={{
                        left: item.cx,
                        ...(isHigh ? { top: item.cy + 58 } : { bottom: STAGE_H - item.cy + 58 }),
                      }}
                      data-cx={item.cx}
                    >
                      <h3 className="cjh-card-title">{item.title}</h3>
                      <p className="cjh-card-desc">{item.desc}</p>
                      {chips.length > 0 && (
                        <div className="cjh-chips">
                          {chips.map((v) => (
                            <button
                              key={v.id}
                              className="cjh-chip"
                              onClick={() => setLightbox(v)}
                              data-testid={`cj-watch-${v.id}`}
                            >
                              <Play size={11} /> Watch {v.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="cjh-finish" style={{ left: TOTAL_W - 60, top: Y_MID - 30 }} data-cx={TOTAL_W - 120}>
                <span className="cjh-finish-ring" />
                <KeyRound size={22} />
                <span className="cjh-finish-label">And repeat.</span>
              </div>
            </div>
          </div>

          <div className="cjh-progress"><div className="cjh-progress-fill" ref={barRef} /></div>
        </div>
      </div>

      {/* Demo video lightbox */}
      {lightbox && (
        <div className="cj-lightbox-overlay" onClick={() => setLightbox(null)} data-testid="cj-lightbox">
          <div className="cj-lightbox" onClick={(e) => e.stopPropagation()}>
            <button className="cj-lightbox-close" onClick={() => setLightbox(null)} data-testid="cj-lightbox-close">
              <X size={18} />
            </button>
            <video
              src={lightbox.url}
              poster={lightbox.poster_url || undefined}
              className="cj-lightbox-video"
              autoPlay
              controls
              playsInline
              data-testid="cj-lightbox-video"
            />
            <span className="cj-lightbox-label">{lightbox.label}</span>
          </div>
        </div>
      )}
    </section>
  );
};

export default CustomerJourney;
